/**
 * NextContactAction (KAN-960)
 *
 * Кнопка "Следующий контакт" и её модалка (NextContactDialog). Одна для всех
 * мест: чат клиента, строки "Требуют внимания", карточка клиента, ячейка
 * "Следующая связь" таблицы CRM (свой триггер: дата-ссылка или "+"),
 * карточка клиента в приложении менеджера. Модалка монтируется только
 * открытой: в строках списков кнопка ничего не загружает. Менеджеру без
 * правила API (KAN-811) кнопки нет.
 */
import { useState, type ReactNode } from 'react';
import { CalendarClock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCanCallApi } from '@/hooks/useApiAccess';
import { NEXT_CONTACT_ACCESS_URL, NEXT_CONTACT_LABEL } from '@/lib/nextContact';
import type { WorkSurface } from '@/lib/workActions';
import type { NextContactTarget } from '@/lib/nextContact';
import { NextContactDialog } from './NextContactDialog';

interface NextContactActionProps {
  target: NextContactTarget;
  /** Свой триггер вместо кнопки (ячейка таблицы); получает открытие модалки. */
  trigger?: (open: () => void) => ReactNode;
  /** Что показать без доступа к API (ячейка таблицы: дата текстом); по умолчанию ничего. */
  fallback?: ReactNode;
  /** Только иконка (подпись в title и aria-label). */
  compact?: boolean;
  disabled?: boolean;
  className?: string;
  surface?: WorkSurface;
}

export function NextContactAction({
  target,
  trigger,
  fallback = null,
  compact = false,
  disabled = false,
  className,
  surface = 'cabinet',
}: NextContactActionProps): JSX.Element {
  const allowed = useCanCallApi('POST', NEXT_CONTACT_ACCESS_URL);
  const [open, setOpen] = useState(false);
  if (!allowed) return <>{fallback}</>;
  const show = () => setOpen(true);

  return (
    <>
      {trigger ? (
        trigger(show)
      ) : (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled}
          className={className}
          title={compact ? NEXT_CONTACT_LABEL : undefined}
          aria-label={NEXT_CONTACT_LABEL}
          data-testid="next-contact-button"
          onClick={show}
        >
          <CalendarClock className={compact ? 'h-3.5 w-3.5' : 'mr-1 h-3.5 w-3.5'} />
          {!compact && NEXT_CONTACT_LABEL}
        </Button>
      )}
      {open && <NextContactDialog target={target} surface={surface} onClose={() => setOpen(false)} />}
    </>
  );
}
